import React, { useEffect } from 'react';
import {
  TranslateFunction,
  useI18n,
  FormatDateFunction,
} from '../../../../hooks/useI18n';
import { Columns, Table } from '../../../table';
import { CustomerType } from '../../../../types';
import { messages } from '../../../../messages';
import { ApiErrorDetails } from '../../../apiErrorDetails/apiErrorDetails';
import { useApiResource } from '../../../../hooks/useApi';
import { ListLayout } from '../../../listLayout/listLayout';
import { useQueryParams, serializers } from '../../../../hooks/useQueryParams';
import { useShopSessionStore } from '../../../../hooks/shop/useShopSessionStore';
import styles from './placedOrders.module.scss';
import { formatNumber } from '../../../../helpers/formatNumber/formatNumber';
import { OrderProgressBadge } from '../../../orderProgressBadge/orderProgressBadge';
import { OrderListItem, ordersApi } from '../../../../api/ordersApi';
import { OrderFilters, OrderQueryParams } from '../orderFilter/orderFilters';
import { fromOrderProgresses } from '../../../../helpers/orderProgressConverter';

const getColumns = (
  t: TranslateFunction,
  formatDate: FormatDateFunction,
  customerIsBrand: boolean,
): Columns<OrderListItem> => [
  {
    header: t(messages.order.order),
    accessor: 'orderNumber',
    sortable: true,
    width: 100,
  },
  {
    header: t(messages.shared.customer),
    accessor: 'customerName',
    sortable: true,
    show: customerIsBrand,
  },
  {
    header: t(messages.order.referenceOrderNumber),
    accessor: 'externalOrderNumber',
    sortable: true,
  },
  {
    header: t(messages.shared.totalPrice),
    accessor: 'orderSum',
    align: 'right',
    cell: ({ row }) => `${formatNumber(row.orderSum, 2)} ${row.currencyCode}`,
    sortable: true,
  },
  {
    header: t(messages.order.orderDate),
    accessor: 'orderDate',
    sortable: true,
    cell: ({ row }) => formatDate(row.orderDate),
  },
  {
    header: <DeliveryDateHeader />,
    accessor: 'deliveryDate',
    sortable: true,
    width: 230,
    cell: ({ row }) => (
      <div>
        <div className={styles.deliveryDateCell}>
          {row.deliveryDate
            ? formatDate(row.deliveryDate)
            : row.deliveryDateNote}
        </div>
        <div className={styles.deliveryDateCell}>
          {row.latestDeliveryDate ? formatDate(row.latestDeliveryDate) : '-'}
        </div>
      </div>
    ),
  },
  {
    header: t(messages.order.progress),
    accessor: 'orderProgress',
    sortable: true,
    width: 150,
    cell: ({ row }) => (
      <OrderProgressBadge
        orderStatus={row.status}
        deliveryStatus={row.deliveryStatus}
      />
    ),
  },
];

const pageSize = 30;

export function PlacedOrders({
  onSelectOrderId,
}: {
  onSelectOrderId: (id: number) => void;
}) {
  const { t, formatDate } = useI18n();
  const shopSessionStore = useShopSessionStore();
  const customerIsBrand =
    shopSessionStore.session.customerType === CustomerType.Brand;
  const columns = getColumns(t, formatDate, customerIsBrand);

  const orders = useApiResource(ordersApi.getOrders, {
    emptyValue: {
      totalItems: 0,
      items: [],
    },
    clearDataOnLoad: false,
  });

  const [queryParams, setQueryParams] = useQueryParams<OrderQueryParams>({
    articleNumber: serializers.string({ defaultValue: '' }),
    customerId: serializers.number(),
    orderProgresses: serializers.numberArray({
      defaultValue: [],
    }),
    orderType: serializers.number(),
    page: serializers.number({ defaultValue: 1 }),
    pageSize: serializers.number({ defaultValue: 20 }),
    placed: serializers.boolean({ defaultValue: true }),
    searchQuery: serializers.string({ defaultValue: '' }),
    sortingDesc: serializers.boolean({ defaultValue: true }),
    sortingKey: serializers.custom<keyof OrderListItem>({
      defaultValue: 'orderDate',
      serialize: (v) => v,
      deserialize: (v) => {
        if (v && columns.some((c) => c.accessor === v)) {
          return v as keyof OrderListItem;
        }
      },
    }),
  });

  useEffect(() => {
    orders.refresh({
      articleNumber: queryParams.articleNumber,
      customerId: customerIsBrand ? queryParams.customerId : undefined,
      orderProgresses: fromOrderProgresses(queryParams.orderProgresses),
      orderType: queryParams.orderType,
      page: queryParams.page,
      pageSize,
      placed: queryParams.placed,
      searchQuery: queryParams.searchQuery,
      sortingDesc: queryParams.sortingDesc,
      sortingKey: queryParams.sortingKey,
    });
  }, [
    queryParams.articleNumber,
    queryParams.customerId,
    queryParams.orderProgresses,
    queryParams.orderType,
    queryParams.page,
    queryParams.pageSize,
    queryParams.placed,
    queryParams.searchQuery,
    queryParams.sortingDesc,
    queryParams.sortingKey,
  ]);

  return (
    <ListLayout>
      <ListLayout.Sider>
        <OrderFilters
          queryParams={queryParams}
          onChangeQueryParams={setQueryParams}
        />
      </ListLayout.Sider>
      <ListLayout.Content>
        <ListLayout.ContentHeader
          title={t(messages.shared.listingItemsCount, {
            count: orders.data.totalItems || 0,
          })}
        />
        {orders.error && (
          <ListLayout.ContentSection>
            <ApiErrorDetails error={orders.error} />
          </ListLayout.ContentSection>
        )}
        <Table<OrderListItem>
          columns={columns}
          items={orders.data.items}
          isLoading={orders.isLoading}
          onSelectItem={(order) => onSelectOrderId(order.id)}
          paged={{
            page: queryParams.page,
            pageSize,
            onPageChange: (page) => {
              setQueryParams({ ...queryParams, page });
            },
            totalItems: orders.data.totalItems || 0,
          }}
          sorted={{
            key: queryParams.sortingKey,
            desc: queryParams.sortingDesc,
            onSortedChange: (sorting) =>
              setQueryParams({
                ...queryParams,
                sortingDesc: sorting.desc,
                sortingKey: sorting.key,
              }),
          }}
        />
      </ListLayout.Content>
    </ListLayout>
  );
}

function DeliveryDateHeader() {
  const { t } = useI18n();

  return (
    <div>
      <div className={styles.deliveryDateGroupHeader}>
        {t(messages.order.deliveryDates)}
      </div>
      <div>
        <div className={styles.deliveryDateCell}>
          {t(messages.order.confirmedDate)}
        </div>
        <div className={styles.deliveryDateCell}>
          {t(messages.order.latestDate)}
        </div>
      </div>
    </div>
  );
}
